import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'umi';

import TagFilterSelect from '@/pages/datalab/components/TagFilterSelect';

import type {
  IConditionalFormatValue,
  IMetricOption,
  INumericalValueItem,
  INumericalValueRule,
} from '../type';
import NumericalValuesGrid from './NumericalValuesGrid';
import {
  createEmptyRule,
  createNumericalGroupFromMetric,
  normalizeNumericalGroups,
  stripNumericalValuesForPersist,
} from './utils';
import './index.scss';

export interface INumericalValuesProps {
  value?: INumericalValueItem[];
  onChange: (value: Partial<IConditionalFormatValue>) => void;
  metrics?: IMetricOption[];
  dimensions?: unknown[];
}

const NumericalValues: React.FC<INumericalValuesProps> = ({
  value,
  onChange,
  metrics,
}) => {
  const { formatMessage } = useIntl();

  const groups = useMemo(() => normalizeNumericalGroups(value), [value]);

  const options = useMemo(
    () =>
      (metrics || []).map((item: IMetricOption) => ({
        ...item,
        value: item.id,
        text: item.column_display,
      })),
    [metrics],
  );

  const metricsValue = useMemo(() => groups.map(group => group.id), [groups]);

  const displayGroups = useMemo(() => {
    const filtered = groups.filter(group =>
      options.some(opt => opt.id === group.id),
    );
    return filtered.map(group => {
      const selectItem = options.find(opt => opt.id === group.id);
      return {
        ...group,
        table_code: group.table_code || selectItem?.table_code || 't0',
        metric: group.metric || selectItem?.column_name,
        name:
          selectItem?.column_display ||
          selectItem?.column_name ||
          group.name ||
          group.metric,
      };
    });
  }, [groups, options]);

  const emitChange = useCallback(
    (next: INumericalValueItem[]) => {
      onChange({
        table_numerical_values: stripNumericalValuesForPersist(next),
      });
    },
    [onChange],
  );

  const handleMetricsChange = useCallback(
    (selectedIds: string[]) => {
      const list = (selectedIds || []).map((id): INumericalValueItem => {
        const found = groups.find(group => group.id === id);
        if (found) {
          const selectItem = options.find(opt => opt.id === id);
          return {
            ...found,
            table_code: found.table_code || selectItem?.table_code || 't0',
            metric: found.metric || selectItem?.column_name,
            name:
              selectItem?.column_display ||
              selectItem?.column_name ||
              found.name ||
              found.metric,
          };
        }
        const selectItem = options.find(opt => opt.id === id);
        return createNumericalGroupFromMetric(id, selectItem);
      });
      emitChange(list);
    },
    [groups, options, emitChange],
  );

  const handleAddRule = useCallback(
    (groupId: string) => {
      const next = displayGroups.map(group => {
        if (group.id !== groupId) {
          return group;
        }
        return {
          ...group,
          rules: [...(group.rules || []), createEmptyRule()],
        };
      });
      emitChange(next);
    },
    [displayGroups, emitChange],
  );

  const handleRuleChange = useCallback(
    (groupId: string, ruleIndex: number, nextRule: INumericalValueRule) => {
      const next = displayGroups.map(group => {
        if (group.id !== groupId) {
          return group;
        }
        const rules = (group.rules || []).map((rule, index) =>
          index === ruleIndex ? nextRule : rule,
        );
        return { ...group, rules };
      });
      emitChange(next);
    },
    [displayGroups, emitChange],
  );

  const handleDeleteRule = useCallback(
    (groupId: string, ruleIndex: number) => {
      const next = displayGroups.flatMap(group => {
        if (group.id !== groupId) {
          return [group];
        }
        const currentRules = group.rules || [];
        // 仅剩一条 rule 时删除，等同于移除整个指标 group
        if (currentRules.length <= 1) {
          return [];
        }
        const rules = currentRules.filter(
          (_rule, index) => index !== ruleIndex,
        );
        return [{ ...group, rules }];
      });
      emitChange(next);
    },
    [displayGroups, emitChange],
  );

  return (
    <div className="dbl-numerical-values">
      <div className="dbl-numerical-values__title">
        <div className="dbl-numerical-values__title-label">
          {formatMessage({ id: 'dbl_table_condition_select_metrics' })}
        </div>
        <div className="dbl-numerical-values__title-desc">
          <TagFilterSelect
            value={metricsValue}
            options={(options || []) as any}
            listWidth={400}
            overlayClassName="dbl-model-tag-select"
            placeholder={formatMessage({
              id: 'dbl_table_condition_select_metrics_placeholder',
            })}
            onChange={handleMetricsChange}
            name={formatMessage({ id: 'dbl_table_metric' })}
            tooltip={undefined}
          />
        </div>
      </div>
      <NumericalValuesGrid
        groups={displayGroups}
        onAddRule={handleAddRule}
        onDeleteRule={handleDeleteRule}
        onRuleChange={handleRuleChange}
      />
    </div>
  );
};

export default NumericalValues;
